import { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import AdminLayout from '@/components/AdminLayout';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, DollarSign, FileText, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';

export default function SalesmanDashboard() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [stats, setStats] = useState({
    totalOffers: 0,
    acceptedOffers: 0,
    totalMargin: 0,
    avgMargin: 0
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const user = await base44.auth.me();
      setCurrentUser(user);

      // Pobranie wszystkich ofert przypisanych do handlowca
      const allOffers = await base44.entities.Offer.list('-created_date', 500);
      const userOffers = (allOffers || []).filter(o => o.assignedTo === user.email);
      
      setOffers(userOffers);

      // Dane do wykresu - oferty w podziale na miesiące
      const monthlyData = buildMonthlyChart(userOffers);
      setChartData(monthlyData);

      // Statystyki
      const acceptedOffers = userOffers.filter(o => o.status === 'Zaakceptowana');
      const totalMargin = acceptedOffers.reduce((sum, o) => {
        const margin = calculateMargin(o);
        return sum + (margin || 0);
      }, 0);
      
      setStats({
        totalOffers: userOffers.length,
        acceptedOffers: acceptedOffers.length,
        totalMargin: totalMargin,
        avgMargin: acceptedOffers.length > 0 ? totalMargin / acceptedOffers.length : 0
      });

      setLoading(false);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      setLoading(false);
    }
  };

  const buildMonthlyChart = (offers) => {
    const months = {};
    
    offers.forEach(offer => {
      const date = new Date(offer.created_date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = date.toLocaleDateString('pl-PL', { month: 'short', year: '2-digit' });
      
      if (!months[monthKey]) {
        months[monthKey] = { month: monthName, accepted: 0, rejected: 0, sent: 0 };
      }
      
      if (offer.status === 'Zaakceptowana') {
        months[monthKey].accepted += 1;
      } else if (offer.status === 'Odrzucona') {
        months[monthKey].rejected += 1;
      } else {
        months[monthKey].sent += 1;
      }
    });

    return Object.values(months).sort();
  };

  const calculateMargin = (offer) => {
    if (!offer.items || offer.items.length === 0) return 0;
    
    // Marża = (cena sprzedaży - koszt) / cena sprzedaży * 100
    // Uproszczenie: zakładając że margin to różnica między totalGross a estimated cost
    const margin = ((offer.totalGross || 0) - (offer.estimatedCost || 0)) / (offer.totalGross || 1) * 100;
    return Math.max(0, margin);
  };

  const StatCard = ({ icon: Icon, label, value, unit = '' }) => (
    <motion.div
      className="border border-gray-200 rounded-xl p-6 bg-white hover:shadow-lg transition-all"
      whileHover={{ translateY: -2 }}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-gray-500 text-sm font-inter font-medium uppercase tracking-wide mb-2">{label}</p>
          <p className="font-inter text-3xl font-bold text-gray-900">
            {typeof value === 'number' ? value.toLocaleString('pl-PL', { maximumFractionDigits: 0 }) : value}
            {unit && <span className="text-lg text-gray-400 ml-1">{unit}</span>}
          </p>
        </div>
        <Icon size={28} className="text-blue-600" />
      </div>
    </motion.div>
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-screen">
          <div className="w-8 h-8 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <motion.div
        className="p-8 font-inter"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="mb-8">
          <h1 className="font-inter text-4xl font-bold text-gray-900 mb-2">Pulpit Handlowca</h1>
          <p className="text-gray-500 text-lg">Analiza skuteczności sprzedaży i wygenerowanej marży</p>
        </div>

        {/* Statistics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={FileText}
            label="Wszystkie Oferty"
            value={stats.totalOffers}
          />
          <StatCard
            icon={CheckCircle}
            label="Zaakceptowane"
            value={stats.acceptedOffers}
          />
          <StatCard
            icon={DollarSign}
            label="Całkowita Marża"
            value={stats.totalMargin.toLocaleString('pl-PL', { maximumFractionDigits: 0 })}
            unit="EUR"
          />
          <StatCard
            icon={TrendingUp}
            label="Średnia Marża"
            value={stats.avgMargin.toLocaleString('pl-PL', { maximumFractionDigits: 1 })}
            unit="%"
          />
        </div>

        {/* Chart Section */}
        <motion.div
          className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm"
          whileHover={{ shadow: 'lg' }}
        >
          <h2 className="font-inter text-2xl font-bold text-gray-900 mb-6">Oferty w podziale na miesiące</h2>
          
          {chartData.length > 0 ? (
            <div className="w-full h-96">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" stroke="#6b7280" />
                  <YAxis stroke="#6b7280" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}
                    labelStyle={{ color: '#1f2937' }}
                  />
                  <Legend />
                  <Bar dataKey="accepted" name="Zaakceptowane" fill="#10b981" radius={[8, 8, 0, 0]} />
                  <Bar dataKey="sent" name="Wysłane" fill="#3b82f6" radius={[8, 8, 0, 0]} />
                  <Bar dataKey="rejected" name="Odrzucone" fill="#ef4444" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex items-center justify-center h-96 text-gray-400">
              <p className="text-lg">Brak danych do wyświetlenia</p>
            </div>
          )}
        </motion.div>

        {/* Recent Offers */}
        <motion.div
          className="mt-8 bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="bg-gray-50 border-b border-gray-200 p-6">
            <h2 className="font-inter text-2xl font-bold text-gray-900">Ostatnie oferty</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-6 py-4 text-left font-inter text-xs font-semibold uppercase tracking-wide text-gray-600">Nr Oferty</th>
                  <th className="px-6 py-4 text-left font-inter text-xs font-semibold uppercase tracking-wide text-gray-600">Klient</th>
                  <th className="px-6 py-4 text-right font-inter text-xs font-semibold uppercase tracking-wide text-gray-600">Wartość (EUR)</th>
                  <th className="px-6 py-4 text-center font-inter text-xs font-semibold uppercase tracking-wide text-gray-600">Status</th>
                  <th className="px-6 py-4 text-right font-inter text-xs font-semibold uppercase tracking-wide text-gray-600">Data</th>
                </tr>
              </thead>
              <tbody>
                {offers.slice(0, 10).map((offer, idx) => (
                  <tr key={offer.id} className={`border-b border-gray-200 hover:bg-gray-50 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                    <td className="px-6 py-4 font-inter font-semibold text-gray-900">{offer.offerNumber}</td>
                    <td className="px-6 py-4 text-gray-700 font-inter">{offer.clientName}</td>
                    <td className="px-6 py-4 text-right font-inter font-medium text-gray-900">
                      {offer.totalGross?.toLocaleString('pl-PL', { minimumFractionDigits: 0 })}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                        offer.status === 'Zaakceptowana' ? 'bg-green-100 text-green-800' :
                        offer.status === 'Wysłana' ? 'bg-blue-100 text-blue-800' :
                        offer.status === 'Odrzucona' ? 'bg-red-100 text-red-800' :
                        offer.status === 'Szkic' ? 'bg-gray-100 text-gray-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {offer.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-500">
                      {new Date(offer.created_date).toLocaleDateString('pl-PL')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      </motion.div>
    </AdminLayout>
  );
}